/* eslint-disable @angular-eslint/no-input-rename */
import { Directive, Input, OnDestroy, OnInit, TemplateRef, ViewContainerRef } from '@angular/core';
import { asyncScheduler, scheduled, Subject, takeUntil } from 'rxjs';
import { FeatureExpression } from '../models/FeatureExpression';
import * as FeaturesStringParser from '../resources/parser-features';
import { FeatureFlagService } from '../services/feature-flag.service';

@Directive({
  selector: '[ifFeature]',
  standalone: true
})
export class IfFeatureDirective implements OnInit, OnDestroy {
  protected hasView: boolean = false;
  protected hasElseView: boolean = false;
  protected condition: boolean = false;
  protected elseTemplateRef: TemplateRef<any> | undefined = undefined;
  private readonly onDestroy: Subject<void> = new Subject<void>();
  constructor(
    protected templateRef: TemplateRef<any>,
    protected viewContainer: ViewContainerRef,
    protected featureFlagService: FeatureFlagService
  ) {}

  @Input('ifFeatureElse') set ifFeatureElse(template: TemplateRef<any>) {
    this.setElseTemplate(template);
  }

  @Input('ifFeature') set ifFeature(featureId: string) {
    if (featureId) {
      scheduled(this.evaluate(featureId), asyncScheduler)
        .pipe(takeUntil(this.onDestroy))
        .subscribe({
          next: (res: boolean) => {
            this.setCondition(res);
          },
          error: () => this.setCondition(false)
        });
    } else {
      // no featureId provided
      this.setCondition(false);
    }
  }

  /* eslint-disable-next-line  @angular-eslint/no-empty-lifecycle-method */
  ngOnInit() {
    // Empty lifecycle necessary in order to ensure setters are called for additional input properties at the right time
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  private async evaluate(featureIdsExpression: string): Promise<boolean> {
    const expression = FeaturesStringParser.parse(featureIdsExpression);
    await this.resolveExpression(expression);
    // wrapping the evaluateExpression in a promise to avoid race condition.
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(this.evaluateExpression(expression));
      });
    });
  }

  public setCondition(condition: boolean) {
    this.condition = condition;
    this.updateContainer();
  }

  private updateContainer() {
    if (this.condition) {
      if (!this.hasView) {
        this.clearContainer();
        if (this.templateRef) {
          this.viewContainer.createEmbeddedView(this.templateRef);
          this.hasView = true;
        }
      }
    } else {
      if (!this.hasElseView) {
        this.clearContainer();
        if (this.elseTemplateRef) {
          this.viewContainer.createEmbeddedView(this.elseTemplateRef);
          this.hasElseView = true;
        }
      }
    }
  }

  private clearContainer() {
    this.viewContainer.clear();
    this.hasView = false;
    this.hasElseView = false;
  }

  public setElseTemplate(template: TemplateRef<any>) {
    this.elseTemplateRef = template;
    this.updateContainer();
  }

  private async resolveExpression(expression: FeatureExpression): Promise<void> {
    if (expression?.type === 'BinaryExpression') {
      this.resolveExpression(expression.left);
      this.resolveExpression(expression.right);
    } else if (expression?.type === 'UnaryExpression') {
      this.resolveExpression(expression.operand);
    } else if (expression?.type === 'feature') {
      try {
        expression.resolved = await this.featureFlagService.isFeatureEnabled(expression.name);
      } catch (e) {
        expression.resolved = false;
        throw new Error(`Could not resolve feature flag: ${expression.name}`);
      }
    }
  }

  private evaluateExpression(expression: FeatureExpression): boolean {
    if (expression?.type === 'BinaryExpression') {
      const leftValue = this.evaluateExpression(expression.left);
      const rightValue = this.evaluateExpression(expression.right);
      switch (expression.operator) {
        case '&&':
          return leftValue && rightValue;
        case '||':
          return leftValue || rightValue;
        default:
          throw new Error(`Unknown binary operator: ${expression.operator}`);
      }
    } else if (expression?.type === 'UnaryExpression') {
      const operandValue = this.evaluateExpression(expression.operand);
      switch (expression.operator) {
        case '!':
          return !operandValue;
        default:
          throw new Error(`Unknown unary operator: ${expression.operator}`);
      }
    } else if (expression?.type === 'feature') {
      return expression.resolved;
    } else {
      throw new Error(`Unknown expression type: ${expression.type}`);
    }
  }
}
